import { CircleInfoIcon } from "@xola/icons";
import { Alert, Checkbox, flash, FormGroup, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import axios from "axios";
import clsx from "clsx";
import { FormField, InputField, nullableString, requiredEmail, requiredString } from "common";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";
import {
    ApiKeyUser,
    Delegate,
    DelegateForm,
    DelegateRole,
    DelegateRoleMapping,
    FeatureRole,
    Permission,
    Seller,
} from "types";
import { array, object } from "yup";
import { CreatedApiKeyModal } from "./CreatedApiKeyModal";
import { RevokeApiKeyButton } from "./RevokeApiKeyButton";
import { RevokeDelegateAccessButton } from "./RevokeDelegateAccessButton";
import { api } from "@/api/api";
import { xolaUri } from "@/api/xolaUri";
import { Form } from "@/components/Common/Form";
import { IsGranted } from "@/components/Common/IsGranted";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResolveAppStorePath } from "@/hooks/usePlugins";
import { hasFeature } from "@/models/Seller";

export type ModalMode = "delegate" | "apiKey";

const roleFeatures: Partial<Record<DelegateRole, { feature: FeatureRole; appName: string; featureId: string }>> = {
    [DelegateRole.WEBSITE_MANAGER]: { feature: FeatureRole.XSITE, appName: "XSite", featureId: "xsite" },
};

const isRoleFeatureEnabled = (seller: Seller, roleId: DelegateRole) => {
    const gate = roleFeatures[roleId];
    return !gate || hasFeature(seller, gate.feature);
};

export const defaultValues = {
    name: "",
    email: "",
    phone: null,
    roles: [],
};

interface ApiKeyForm {
    id?: string;
    name: string;
    roles: string[];
}

export const apiKeyDefaultValues: ApiKeyForm = {
    name: "",
    roles: [],
};

interface DelegateModalProps {
    value?: Delegate | ApiKeyUser | null;
    isOpen: boolean;
    onClose: () => void;
    title: string;
    mode?: ModalMode;
}

export const DelegateModal = ({ value, isOpen, onClose, title, mode = "delegate" }: DelegateModalProps) => {
    return <ModalContent value={value} title={title} mode={mode} isOpen={isOpen} onClose={onClose} />;
};

const delegateValidationSchema = object().shape({
    name: requiredString(),
    email: requiredEmail(),
    phone: nullableString(),
    roles: array().min(1, "should have at least 1 role"),
});

const apiKeyValidationSchema = object().shape({
    name: requiredString(),
    roles: array().min(1, "should have at least 1 role"),
});

const ModalContent = ({ onClose, value, title, mode = "delegate", isOpen }: DelegateModalProps) => {
    const seller = useSeller();
    const user = useUser();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [createdApiKeyId, setCreatedApiKeyId] = useState<string | null>(null);

    const isApiKeyMode = mode === "apiKey";
    const canInstallApps = isGranted(Permission.APP_STORE_VIEW);
    const hasUninstalledRoleFeature = DelegateRoleMapping.some((role) => !isRoleFeatureEnabled(seller, role.id));
    const resolveAppStorePath = useResolveAppStorePath(canInstallApps && hasUninstalledRoleFeature);
    const delegate = isApiKeyMode ? null : (value as Delegate | null);
    const apiKeyValue = isApiKeyMode ? (value as ApiKeyUser | null) : null;

    const existingRoles = isApiKeyMode ? apiKeyValue?.roles ?? [] : delegate?.permissions?.map((x) => x.name) ?? [];
    const temporaryRolesRef = useRef<string[]>(existingRoles);

    const { mutateAsync: saveDelegate, isLoading: isSaving } = useMutation(async (payload: DelegateForm) => {
        if (payload.id) {
            return api.put<Delegate>(`/users/${seller.id}/delegates/${payload.id}`, payload);
        }

        return api.post<Delegate>(`/users/${seller.id}/delegates`, payload);
    });

    const { mutateAsync: saveApiKey, isLoading: isApiKeySaving } = useMutation(async (payload: ApiKeyForm) => {
        if (payload.id) {
            return api.put<ApiKeyUser>(`/sellers/${seller.id}/apiKeys/${payload.id}`, payload);
        }

        return api.post<ApiKeyUser>(`/sellers/${seller.id}/apiKeys`, payload);
    });

    const buildPermissionUrl = (entityType: "delegates" | "apiKeys", id: ID, role: string) => {
        const basePath = entityType === "delegates" ? `/users/${seller.id}` : `/sellers/${seller.id}`;
        return `${basePath}/${entityType}/${id}/permissions/${role}`;
    };

    const setRoles = async (roles: string[], id: ID, entityType: "delegates" | "apiKeys" = "delegates") => {
        if (!roles?.length) return;
        await Promise.all(roles.map(async (role) => api.put(buildPermissionUrl(entityType, id, role), { name: role })));
    };

    const unsetRoles = async (roles: string[], id: ID, entityType: "delegates" | "apiKeys" = "delegates") => {
        if (!roles?.length) return;
        await Promise.all(roles.map(async (role) => api.delete(buildPermissionUrl(entityType, id, role))));
    };

    const getDelegateInitialValues = (): DelegateForm => {
        if (delegate) {
            const { user } = delegate;
            return { ...user, roles: delegate.permissions.map((x) => x.name) };
        }
        return defaultValues;
    };

    const getApiKeyInitialValues = (): ApiKeyForm => {
        if (apiKeyValue) {
            return { id: apiKeyValue.id, name: apiKeyValue.name, roles: apiKeyValue.roles };
        }
        return apiKeyDefaultValues;
    };

    const initialValues = isApiKeyMode ? getApiKeyInitialValues() : getDelegateInitialValues();

    const { mutateAsync: updateRoles, isLoading: isRolesSaving } = useMutation(
        async (payload: { roles: string[]; id: ID; entityType: "delegates" | "apiKeys" }) => {
            const { roles, id, entityType } = payload;
            if (!roles) return;

            const currentRoles = initialValues.roles ?? [];
            const rolesToRemove = currentRoles.filter((x) => !roles.includes(x));
            const rolesToAdd = roles.filter((x) => !currentRoles.includes(x));

            await unsetRoles(rolesToRemove, id, entityType);
            await setRoles(rolesToAdd, id, entityType);
        },
    );

    const handleDelegateSubmit = async (values: DelegateForm) => {
        let id: ID;

        if (delegate?.id) {
            id = delegate.id;
        } else {
            const response = await saveDelegate(values);
            id = response.data.id;
        }

        await updateRoles({ roles: values.roles, id, entityType: "delegates" });

        invalidate("delegates");
        onClose();
    };

    const handleApiKeySubmit = async (values: ApiKeyForm) => {
        if (apiKeyValue) {
            if (values.name !== apiKeyValue.name) {
                await saveApiKey(values);
            }

            const existingRoles = new Set(apiKeyValue.roles);
            const newRoles = new Set(values.roles);
            const rolesChanged =
                existingRoles.size !== newRoles.size || [...existingRoles].some((role) => !newRoles.has(role));

            if (rolesChanged) {
                await updateRoles({ roles: values.roles, id: apiKeyValue.id, entityType: "apiKeys" });
            }

            invalidate("apiKeys");
            flash.success("API Key updated");
            onClose();
            return;
        }

        const { data } = await saveApiKey(values);
        await updateRoles({ roles: values.roles, id: data.id, entityType: "apiKeys" });
        setCreatedApiKeyId(data.id);
    };

    const handleSubmit = async (values: DelegateForm | ApiKeyForm) => {
        await (isApiKeyMode ? handleApiKeySubmit(values as ApiKeyForm) : handleDelegateSubmit(values as DelegateForm));
    };

    const handleChange = (
        role: string,
        _roles: string[],
        setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    ) => {
        let roles = [..._roles];

        const isOnlyAdminRole = roles.length === 1 && roles.includes(DelegateRole.SELLER_ADMIN);

        const isOnlyAdminRoleInRef =
            temporaryRolesRef.current.length === 1 && temporaryRolesRef.current.includes(DelegateRole.SELLER_ADMIN);

        const index = roles.indexOf(role);

        if (roles.includes(DelegateRole.GUIDE) && role === DelegateRole.SELLER_ADMIN) {
            roles = roles.includes(DelegateRole.SELLER_ADMIN) ? [DelegateRole.GUIDE] : [role, DelegateRole.GUIDE];
        } else if (role === DelegateRole.SELLER_ADMIN) {
            if (isOnlyAdminRole) {
                roles = isOnlyAdminRoleInRef ? [] : temporaryRolesRef.current;
            } else if (index > -1) {
                roles = roles.filter((element) => element !== role);
            } else {
                roles = [role];
            }

            temporaryRolesRef.current = roles;
        }

        if (role !== DelegateRole.SELLER_ADMIN) {
            if (index > -1) {
                roles.splice(index, 1);
            } else {
                roles.push(role);
            }

            temporaryRolesRef.current = roles;
        }

        setFieldValue("roles", roles);
    };

    const { mutateAsync: resetPassword } = useMutation(async (payload: string) => {
        try {
            const apiEndpoint = `${xolaUri}/resetting/send-email`;
            const bodyFormData = new FormData();
            bodyFormData.append("username", payload);
            await axios({
                method: "post",
                url: apiEndpoint,
                data: bodyFormData,
                headers: { "content-type": "application/x-www-form-urlencoded" },
            });
            flash.success("Password resetting link send to mail successfully");
        } catch {
            flash.danger("Error resetting password");
        }
    });

    const handleResetPasswordClick = async () => {
        if (delegate) {
            await resetPassword(delegate.user.email);
        }
    };

    const { mutateAsync: resendInvite } = useMutation(async (delegateId: ID) => {
        return api.get(`/users/${delegateId}/resendConfirmation`);
    });

    const handleResendInvite = async () => {
        if (delegate) {
            await resendInvite(delegate.user.id);
            flash.success(`Email confirmation re-sent to ${delegate.user.email}`);
        }
    };

    const handleCreatedApiKeyClose = () => {
        invalidate("apiKeys");
        setCreatedApiKeyId(null);
        onClose();
    };

    // Show the created API key modal after successful creation
    if (createdApiKeyId) {
        return <CreatedApiKeyModal apiKeyId={createdApiKeyId} isOpen={isOpen} onClose={handleCreatedApiKeyClose} />;
    }

    const currentValidationSchema = isApiKeyMode ? apiKeyValidationSchema : delegateValidationSchema;
    const isLoading = isSaving || isRolesSaving || isApiKeySaving;
    const isEditingDelegate = !isApiKeyMode && delegate?.id;
    const isEditingApiKey = isApiKeyMode && apiKeyValue?.id;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <XolaFormik
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={currentValidationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ values, handleSubmit, setFieldValue }) => (
                    <>
                        <Modal.Header>{title}</Modal.Header>

                        <Form>
                            <Modal.Body>
                                <>
                                    {isApiKeyMode && (
                                        <Alert color="primary" className="mb-4">
                                            <div className="flex items-center space-x-3">
                                                <CircleInfoIcon className="mt-0 flex-shrink-0" size="medium" />
                                                <div className="text-sm">
                                                    <strong>Need access to multiple accounts?</strong>{" "}
                                                    <a
                                                        href="https://developers.xola.com/docs/integrate-with-xola"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="underline hover:no-underline"
                                                    >
                                                        Build an app on the Xola App Store
                                                    </a>{" "}
                                                    instead of using individual API keys. It’s the most secure and
                                                    efficient way to manage multi-account integrations.
                                                </div>
                                            </div>
                                        </Alert>
                                    )}
                                    <FormField name="name" label="Name">
                                        <InputField
                                            isRequired
                                            placeholder="Name"
                                            disabled={!isApiKeyMode && !!delegate}
                                        />
                                    </FormField>
                                    {!isApiKeyMode && (
                                        <>
                                            <FormField name="email" label="Email">
                                                <InputField isRequired placeholder="Email" disabled={!!delegate} />
                                            </FormField>
                                            <FormField name="phone" label="Phone">
                                                <InputField placeholder="Phone" disabled={!!delegate} />
                                            </FormField>
                                        </>
                                    )}
                                    <FormGroup data-testid="delegate-modal-roles-form-group">
                                        <FormField name="roles" label="Roles">
                                            {DelegateRoleMapping.map((role) => (
                                                <DelegateInput
                                                    key={`${role.id}-${role.name}`}
                                                    values={values as DelegateForm}
                                                    isChoiceDisabled={
                                                        !isApiKeyMode && delegate?.user.email === user.email
                                                    }
                                                    isFeatureEnabled={isRoleFeatureEnabled(seller, role.id)}
                                                    canInstallApps={canInstallApps}
                                                    resolveAppStorePath={resolveAppStorePath}
                                                    role={role}
                                                    onChange={() => {
                                                        handleChange(role.id, values.roles, setFieldValue);
                                                    }}
                                                />
                                            ))}
                                        </FormField>
                                    </FormGroup>

                                    {isEditingDelegate && (
                                        <>
                                            <div className="pb-1 text-sm font-bold">
                                                Access
                                                <Tooltip content="Manage other users accesses">
                                                    <CircleInfoIcon />
                                                </Tooltip>
                                            </div>
                                            <div className="flex space-x-4">
                                                {!delegate?.user.enabled && (
                                                    <SecondaryButton
                                                        size="small"
                                                        data-testid="delegate-item-resend-invite-revoke-access-button"
                                                        onClick={handleResendInvite}
                                                    >
                                                        Resend Invite
                                                    </SecondaryButton>
                                                )}

                                                <IsGranted permissions={Permission.MODIFY_ACCESS_MANAGER_REVOKE_ACCESS}>
                                                    <RevokeDelegateAccessButton
                                                        delegate={delegate}
                                                        onSuccess={onClose}
                                                    />
                                                </IsGranted>

                                                {delegate?.user.enabled && (
                                                    <SecondaryButton size="small" onClick={handleResetPasswordClick}>
                                                        Reset Password
                                                    </SecondaryButton>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {isEditingApiKey && (
                                        <>
                                            <div className="pb-1 text-sm font-bold">
                                                Access
                                                <Tooltip content="Manage API key access">
                                                    <CircleInfoIcon />
                                                </Tooltip>
                                            </div>
                                            <div className="flex space-x-4">
                                                <IsGranted permissions={Permission.MODIFY_ACCESS_MANAGER_REVOKE_ACCESS}>
                                                    <RevokeApiKeyButton apiKey={apiKeyValue} onSuccess={onClose} />
                                                </IsGranted>
                                            </div>
                                        </>
                                    )}
                                </>
                            </Modal.Body>

                            <Modal.Footer className="space-x-4">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                                    Save Changes
                                </SubmitButton>
                            </Modal.Footer>
                        </Form>
                    </>
                )}
            </XolaFormik>
        </Modal>
    );
};

interface DelegateInputProps {
    values: DelegateForm;
    role: typeof DelegateRoleMapping[any];
    isChoiceDisabled: boolean;
    isFeatureEnabled: boolean;
    canInstallApps: boolean;
    resolveAppStorePath: (featureId?: string) => string;
    onChange: () => void;
}

const DelegateInput = (props: DelegateInputProps) => {
    return <InputField as={() => <RoleCheckbox {...props} />} />;
};

const RoleCheckbox = ({
    values,
    role,
    isChoiceDisabled,
    isFeatureEnabled,
    canInstallApps,
    resolveAppStorePath,
    onChange,
}: DelegateInputProps) => {
    const { message, isDisabled } = getDisableReason({
        values,
        role,
        isChoiceDisabled,
        isFeatureEnabled,
        canInstallApps,
        resolveAppStorePath,
    });

    return (
        <Tooltip interactive content={message} disabled={!message}>
            <Checkbox
                className="mb-2 !items-start"
                checked={values.roles.includes(role.id)}
                label={<CheckboxLabel name={role.name} text={role.text} isDisabled={isDisabled} />}
                disabled={isDisabled}
                onChange={onChange}
            />
        </Tooltip>
    );
};

const CheckboxLabel = ({ name, text, isDisabled }: { name: string; text: string; isDisabled: boolean }) => {
    return (
        <div className={clsx("text-sm leading-4", { "text-gray": isDisabled })}>
            <strong>{name}</strong> <span>{text}</span>
        </div>
    );
};

interface DisabledReasonProps {
    reason: React.ReactNode;
    link?: { to: string; label: React.ReactNode };
}

const DisabledReason = ({ reason, link }: DisabledReasonProps) => (
    <div className="space-y-1">
        <div>{reason}</div>
        {link && (
            <Link to={link.to} className="text-blue-lighter underline hover:no-underline">
                {link.label}
            </Link>
        )}
    </div>
);

const getDisableReason = ({
    values,
    role,
    isChoiceDisabled,
    isFeatureEnabled,
    canInstallApps,
    resolveAppStorePath,
}: Omit<DelegateInputProps, "onChange">): { message: React.ReactNode; isDisabled: boolean } => {
    const isValuesIncludeAdminRole =
        values.roles.includes(DelegateRole.SELLER_ADMIN) && role.id !== DelegateRole.SELLER_ADMIN;

    if (isChoiceDisabled || isValuesIncludeAdminRole || values.roles.includes(DelegateRole.AFFILIATE)) {
        return {
            message: null,
            isDisabled: true,
        };
    }

    if (!isFeatureEnabled) {
        const { appName, featureId } = roleFeatures[role.id] ?? {};
        return {
            isDisabled: true,
            message: (
                <DisabledReason
                    reason={
                        <>
                            The <var data-var="appName">{appName}</var> app is not installed on this account.
                        </>
                    }
                    link={
                        canInstallApps
                            ? {
                                  to: resolveAppStorePath(featureId),
                                  label: (
                                      <>
                                          Install the <var data-var="appName">{appName}</var> app
                                      </>
                                  ),
                              }
                            : undefined
                    }
                />
            ),
        };
    }

    if (role.id === DelegateRole.GUIDE) {
        return {
            isDisabled: true,
            message: (
                <DisabledReason
                    reason="Guides are added from Guide Management."
                    link={{ to: "/resources/guides", label: "Go to Resources > Guide Management" }}
                />
            ),
        };
    }

    return {
        message: null,
        isDisabled: false,
    };
};
